import React, { useEffect, useMemo, useState } from 'react';

import { useParams, useLocation } from 'react-router-dom';

import Form from 'react-bootstrap/Form';
import Card from 'react-bootstrap/Card';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import AsyncSelect from 'react-select';
import Select from 'react-select';
import Required from '../../../Required';
import useToast from '../../../../hooks/useToast';
import { years } from '../../../../helpers';
import useAPI from '../../../../hooks/useAPI';
import ConfirmModal from '../../../common/ConfirmModal';
import useUserContext from '../../../../hooks/useUserContext';
import { getActionsModel } from '../../../../services/roles/factdataRoleStatus';
import StatusBadge from '../../../common/StatusBadge';

export default function CreateNiokr({ onHide, editID }) {
  const api = useAPI();
  const addToast = useToast();
  const location = useLocation();
  const userCtx = useUserContext();
  useEffect(() => {
    if (!editID && location.search.includes('?card=1') && !location.pathname.includes('projs')) {
      onHide();
    }
  }, []);
  const { stratId, portfolioId, projId } = useParams();
  const [request, setRequest] = useState({ type: 'niokr' });
  const [members, setMembers] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const [id, setID] = useState(editID);
  const can = useMemo(() => getActionsModel(request.status, userCtx, stratId, portfolioId, projId ?? request.projId), [request, userCtx]);

  function handleChange(value, key) {
    if (key == 'number') setID(null);
    const obj = { ...request };
    obj[key] = value;
    setRequest(obj);
  }

  async function onSubmit() {
    const obj = { ...request };
    if (!obj.name || !obj.fundName || !obj.topicName || !obj.headId) {
      addToast.error('Заполните все поля');
      return;
    }
    if (projId) obj.projId = projId;
    if (id) obj.id = id;
    const result = await api.post('/factdata/requests', obj);
    if (result.id) {
      setRequest((prev) => ({ ...prev, id: result.id, status: result.status }));
      addToast.success('Заявка сохранена');
      setID(result.id);
    }
  }

  async function search(value) {
    if (!value) return;
    const result = await api.get(`/factdata/teammemberssearch/?projId=${projId}&portfId=${portfolioId}&name=${value}`);
    setMembers(result);
  }

  const onSend = async () => {
    await onSubmit();
    const response = await api.post(`/factdata/requests/${id}/send`);
    if (!response.error) {
      addToast.success('Заявка отправлена на согласование');
      onHide();
    } else addToast.error(response?.error?.message || 'Не удалось произвести операцию');
  };

  async function onRemove() {
    if (!editID) return;
    const result = await api.del(`/factdata/requests/${id}`);
    if (result.id) {
      addToast.success('Заявка удалена');
      onHide();
    } else {
      addToast.error('Ошибка');
    }
  }

  useEffect(async () => {
    if (editID) {
      const result = await api.get(`/factdata/requests/${editID}`);
      setRequest(result.request);
      setMembers([result.request.head]);
    }
  }, [editID]);

  return (
    <>
      <Card className="mt-4 mb-4">
        <Card.Header className="p-4" style={{ backgroundColor: cardHeaderColor }}>
          <div className="d-flex justify-content-between">
            <div>Заявка на НИОКР</div>
            <div>
              <StatusBadge type="request" status={request?.status} />
            </div>
          </div>
        </Card.Header>

        <Card.Body className="p-4">
          <Row className="mb-4">
            <Col md={3}>
              <Form.Group>
                <Form.Label style={styledLabel}>
                  Номер заявки
                  {' '}
                  <Required />
                </Form.Label>
                <Form.Control
                  disabled={editID}
                  value={request.number}
                  onChange={(e) => handleChange(e.target.value, 'number')}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-4">
            <Form.Group>
              <Form.Label style={styledLabel}>
                Название конкурса на проведение исследований/стипендиальной программы
                {' '}
                <Required />
              </Form.Label>
              <Form.Control
                disabled={!can.Edit}
                value={request.name}
                onChange={(e) => handleChange(e.target.value, 'name')}
                as="textarea"
                rows={3}
              />
            </Form.Group>
          </Row>

          <Row className="mb-4">
            <Form.Group>
              <Form.Label style={styledLabel}>
                Название фонда/организации
                {' '}
                <Required />
              </Form.Label>
              <Form.Control
                disabled={!can.Edit}
                value={request.fundName}
                onChange={(e) => handleChange(e.target.value, 'fundName')}
                placeholder="Название фонда, например, РНФ, РФФИ, фонда содействия инновациям, фонд Потанина, фонд Комиссарова и т.д."
              />
            </Form.Group>
          </Row>

          <Row className="mb-4">
            <Form.Group>
              <Form.Label style={styledLabel}>
                Тема проекта
                {' '}
                <Required />
              </Form.Label>
              <Form.Control
                disabled={!can.Edit}
                value={request.topicName}
                onChange={(e) => handleChange(e.target.value, 'topicName')}
                placeholder="Указывается тема, поданная для участия в конкурсе."
              />
            </Form.Group>
          </Row>

          <Row className="mb-2">
            <Col md={8}>
              <Form.Group>
                <Form.Label style={styledLabel}>
                  Руководитель темы заявки на НИОКР
                  {' '}
                  <Required />
                </Form.Label>
                <AsyncSelect
                  classNamePrefix=" selector_input_base"
                  isDisabled={!can.Edit}
                  placeholder={
                    members?.length === 1
                      ? `${members[0]?.lastName
                      } ${
                        members[0]?.firstName
                      } ${
                        members[0]?.middleName
                      } (${
                        members[0]?.post
                      })`
                      : ''
                  }
                  onInputChange={(e) => {
                    search(e);
                  }}
                  onChange={(e, a) => {
                    handleChange(e?.id, 'headId');
                    if (a.action === 'clear') {
                      setMembers([]);
                      handleChange(null, 'headId');
                    }
                  }}
                  options={members.map((e) => {
                    e.value = e.id;
                    e.label = `${e.lastName} ${e.firstName} ${e.middleName} (${e.post})`;
                    return e;
                  })}
                  isClearable
                  noOptionsMessage={() => 'Ничего не найдено'}
                  loadingMessage={() => 'Поиск...'}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label style={styledLabel}>
                  Год подачи заявки
                  {' '}
                  <Required />
                </Form.Label>
                <Select
                  classNamePrefix=" selector_input_base"
                  placeholder={request.year || ''}
                  isDisabled={id}
                  onChange={(e) => handleChange(e.value, 'year')}
                  options={years
                    .filter((e) => e !== 2021)
                    .map((e) => ({ value: e, label: e }))}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Card className="p-4">
        <Form.Group as={Col}>
          <Button onClick={onHide} variant="outline-secondary">
            Отменить
          </Button>
          {' '}
          {can.Delete && editID && (
            <>
              <Button onClick={() => setConfirm(true)} variant="outline-danger">
                Удалить
              </Button>
              <ConfirmModal
                show={confirm}
                onClose={() => setConfirm(false)}
                onConfirm={onRemove}
                headerText="Подтверждение операции"
                message="Данная заявка будет удалена. Вы уверены?"
              />
            </>
          )}
          {' '}
          {can.Edit && (
          <Button onClick={onSubmit} variant="primary">
            Сохранить
          </Button>
          )}
          {' '}
          {can.Send && (
          <Button variant="primary" onClick={onSend}>
            Отправить на согласование
          </Button>
          )}
          {' '}
        </Form.Group>
      </Card>
    </>
  );
}

const styledHeading = { marginBottom: 24, textAlign: 'left' };
const cardHeaderColor = '#F7F7F7';
const styledLabel = { color: '#7F7F7F', fontWeight: '600' };
